import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  Stack,
  Tabs,
  Tab,
  TextField,
  Typography,
  useTheme,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Backdrop,
} from '@mui/material';
import { tokens } from '../../../theme';

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box pt={2}>{children}</Box>;
}

const DEFAULT_TIME_UTC = '00:00';

function combineUtcDateAndTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;

  const iso = `${dateStr}T${timeStr}:00Z`;
  const dt = new Date(iso);

  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}

function formatSchedulePreview(dateStr, timeStr) {
  const dt = combineUtcDateAndTime(dateStr, timeStr);
  if (!dt) return null;

  return {
    utcIso: dt.toISOString(),
    utcLabel: dt.toUTCString(),
    localLabel: dt.toLocaleString(),
    localTimeZone:
      Intl.DateTimeFormat().resolvedOptions().timeZone || 'Local browser time',
  };
}

const CreateReleaseDialog = ({
  open,
  onClose,
  productsByRelease,
  onCreate,
  isSubmitting = false,
  submitError = '',
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [tab, setTab] = useState(0);
  const [releaseName, setReleaseName] = useState('');
  const [notes, setNotes] = useState('');

  const [includeContact, setIncludeContact] = useState(true);
  const [includeDigital, setIncludeDigital] = useState(true);

  const [contactScheduledDate, setContactScheduledDate] = useState('');
  const [digitalScheduledDate, setDigitalScheduledDate] = useState('');

  const [contactScheduledTimeUtc, setContactScheduledTimeUtc] = useState(DEFAULT_TIME_UTC);
  const [digitalScheduledTimeUtc, setDigitalTimeUtc] = useState(DEFAULT_TIME_UTC);
  const initializedOpenRef = useRef(false);

  const [included, setIncluded] = useState({ Contact: new Set(), Digital: new Set() });

  useEffect(() => {
    if (!open) {
      initializedOpenRef.current = false;
      return;
    }

    // Prevent refetch/polling updates from nuking the form while user is typing.
    if (initializedOpenRef.current) return;
    initializedOpenRef.current = true;

    setTab(0);
    setReleaseName('');
    setNotes('');

    setIncludeContact(true);
    setIncludeDigital(true);

    setContactScheduledDate('');
    setDigitalScheduledDate('');
    setContactScheduledTimeUtc(DEFAULT_TIME_UTC);
    setDigitalTimeUtc(DEFAULT_TIME_UTC);

    const c = new Set((productsByRelease?.Contact || []).map((p) => String(p.dataAssetId)));
    const d = new Set((productsByRelease?.Digital || []).map((p) => String(p.dataAssetId)));

    setIncluded({ Contact: c, Digital: d });
  }, [open, productsByRelease]);

  const counts = useMemo(() => {
    const cTotal = (productsByRelease?.Contact || []).length;
    const dTotal = (productsByRelease?.Digital || []).length;

    return {
      Contact: { total: cTotal, selected: included.Contact.size },
      Digital: { total: dTotal, selected: included.Digital.size },
    };
  }, [productsByRelease, included]);

  const contactPreview = useMemo(
    () => formatSchedulePreview(contactScheduledDate, contactScheduledTimeUtc),
    [contactScheduledDate, contactScheduledTimeUtc]
  );

  const digitalPreview = useMemo(
    () => formatSchedulePreview(digitalScheduledDate, digitalScheduledTimeUtc),
    [digitalScheduledDate, digitalScheduledTimeUtc]
  );

  const canCreate =
    !isSubmitting &&
    Boolean(releaseName.trim()) &&
    ((includeContact && Boolean(contactPreview)) ||
      (includeDigital && Boolean(digitalPreview))) &&
    (includeContact || includeDigital);

  const isIncluded = (releaseType, dataAssetId) =>
    included?.[releaseType]?.has(String(dataAssetId));

  const toggleIncluded = (releaseType, dataAssetId) => {
    if (isSubmitting) return;

    const key = String(dataAssetId);
    setIncluded((prev) => {
      const next = { Contact: new Set(prev.Contact), Digital: new Set(prev.Digital) };
      if (next[releaseType].has(key)) next[releaseType].delete(key);
      else next[releaseType].add(key);
      return next;
    });
  };

  const renderProductList = (releaseType) => {
    const items = productsByRelease?.[releaseType] || [];
    const trackEnabled = releaseType === 'Contact' ? includeContact : includeDigital;

    if (!items.length) {
      return (
        <Typography variant="caption" sx={{ color: colors.grey[300] }}>
          No products found for {releaseType}.
        </Typography>
      );
    }

    return (
      <List dense sx={{ width: '100%', opacity: trackEnabled ? 1 : 0.5 }}>
        {items.map((p) => {
          const active = isIncluded(releaseType, p.dataAssetId);

          return (
            <ListItem
              key={String(p.dataAssetId)}
              sx={{
                border: `1px solid ${colors.primary[300]}`,
                borderRadius: 1,
                mb: 1,
                background: colors.primary[500],
                opacity: active ? 1 : 0.45,
              }}
            >
              <ListItemText
                primary={
                  <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                    <Typography sx={{ fontWeight: 'bold', color: colors.blueAccent[300] }}>
                      {p.name}
                    </Typography>

                    <Chip
                      size="small"
                      label={active ? 'Included' : 'Excluded'}
                      sx={{
                        bgcolor: active ? colors.greenAccent[700] : colors.redAccent[700],
                        color: colors.grey[100],
                      }}
                    />

                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Latest: ${p.latestVersion || '-'}`}
                      sx={{ color: colors.grey[100] }}
                    />
                  </Box>
                }
                secondary={
                  p.latestTimestampUtc ? (
                    <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                      Latest built: {new Date(p.latestTimestampUtc).toLocaleString()}
                    </Typography>
                  ) : (
                    <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                      No version metadata found yet
                    </Typography>
                  )
                }
              />

              <ListItemSecondaryAction>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!trackEnabled || isSubmitting}
                  onClick={() => toggleIncluded(releaseType, p.dataAssetId)}
                  sx={{
                    minWidth: 88,
                    backgroundColor: active ? colors.redAccent[600] : colors.blueAccent[200],
                    color: colors.grey[100],
                    border: `1px solid ${active ? colors.redAccent[400] : colors.blueAccent[100]}`,
                    '&:hover': {
                      backgroundColor: active ? colors.redAccent[500] : colors.blueAccent[100],
                    },
                  }}
                >
                  {active ? 'Remove' : 'Re-add'}
                </Button>
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
      </List>
    );
  };

  const handleDialogClose = (_, reason) => {
    if (isSubmitting) return;
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      onClose();
      return;
    }
    onClose();
  };

  const handleCreate = async () => {
    const makeItems = (releaseType) => {
      const list = productsByRelease?.[releaseType] || [];
      return list
        .filter((p) => isIncluded(releaseType, p.dataAssetId))
        .map((p) => ({
          data_asset_id: p.dataAssetId,
          product: p.name,
          version: '',
          confirmed: false,
        }));
    };

    const payload = {
      name: releaseName.trim(),
      status: 'draft',
      notes: notes || undefined,

      contact: {
        release: 'Contact',
        enabled: includeContact,
        scheduled_release_date_utc: includeContact ? contactPreview?.utcIso : undefined,
        items: includeContact ? makeItems('Contact') : [],
      },

      digital: {
        release: 'Digital',
        enabled: includeDigital,
        scheduled_release_date_utc: includeDigital ? digitalPreview?.utcIso : undefined,
        items: includeDigital ? makeItems('Digital') : [],
      },
    };

    await onCreate(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isSubmitting}
    >
      <DialogTitle sx={{ position: 'relative', pr: 6 }}>
        Create Monthly Release
      </DialogTitle>

      {isSubmitting && <LinearProgress />}

      <DialogContent dividers sx={{ position: 'relative' }}>
        <Backdrop
          open={isSubmitting}
          sx={{
            position: 'absolute',
            zIndex: 2,
            color: '#fff',
            backgroundColor: 'rgba(0,0,0,0.28)',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <CircularProgress color="inherit" size={32} />
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Creating release...
          </Typography>
          <Typography variant="caption">
            Building release record and loading it into the workspace.
          </Typography>
        </Backdrop>

        <Stack spacing={2} sx={{ opacity: isSubmitting ? 0.6 : 1 }}>
          {submitError ? (
            <Alert severity="error" variant="filled">
              {submitError}
            </Alert>
          ) : null}

          <TextField
            size="small"
            label="Release Name"
            value={releaseName}
            onChange={(e) => setReleaseName(e.target.value)}
            sx={{ minWidth: 260 }}
            placeholder="March 2026 Release"
            disabled={isSubmitting}
          />

          <TextField
            size="small"
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            disabled={isSubmitting}
          />

          <Divider />

          <Box
            sx={{
              p: 2,
              border: `1px solid ${colors.primary[300]}`,
              borderRadius: 1,
              background: colors.primary[500],
            }}
          >
            <Stack spacing={2}>
              <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-start">
                <FormControlLabel
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      color: includeContact ? colors.greenAccent[200] : colors.grey[200],
                      fontWeight: 600,
                    },
                  }}
                  control={
                    <Checkbox
                      checked={includeContact}
                      onChange={(e) => setIncludeContact(e.target.checked)}
                      disabled={isSubmitting}
                      sx={{
                        color: colors.greenAccent[700],
                        '&.Mui-checked': {
                          color: colors.greenAccent[400],
                        },
                        '&:hover': {
                          backgroundColor: colors.greenAccent[900],
                        },
                      }}
                    />
                  }
                  label="Include Contact Release"
                />

                <TextField
                  size="small"
                  label="Contact Release Day (UTC)"
                  type="date"
                  value={contactScheduledDate}
                  onChange={(e) => setContactScheduledDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={!includeContact || isSubmitting}
                  sx={{ minWidth: 220 }}
                />

                <TextField
                  size="small"
                  label="Contact Release Time (UTC)"
                  type="time"
                  value={contactScheduledTimeUtc}
                  onChange={(e) => setContactScheduledTimeUtc(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={!includeContact || isSubmitting}
                  inputProps={{ step: 300 }}
                  sx={{ minWidth: 180 }}
                />

                <Chip
                  size="small"
                  label={`Contact: ${counts.Contact.selected}/${counts.Contact.total}`}
                />
              </Box>

              {includeContact && contactPreview && (
                <Box sx={{ pl: 0.5 }}>
                  <Typography variant="caption" sx={{ display: 'block', color: colors.grey[200] }}>
                    UTC: {contactPreview.utcIso}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: colors.grey[300] }}>
                    Your time ({contactPreview.localTimeZone}): {contactPreview.localLabel}
                  </Typography>
                </Box>
              )}

              <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-start">
                <FormControlLabel
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      color: includeDigital ? colors.greenAccent[200] : colors.grey[200],
                      fontWeight: 600,
                    },
                  }}
                  control={
                    <Checkbox
                      checked={includeDigital}
                      onChange={(e) => setIncludeDigital(e.target.checked)}
                      disabled={isSubmitting}
                      sx={{
                        color: colors.greenAccent[700],
                        '&.Mui-checked': {
                          color: colors.greenAccent[400],
                        },
                        '&:hover': {
                          backgroundColor: colors.greenAccent[900],
                        },
                      }}
                    />
                  }
                  label="Include Digital Release"
                />

                <TextField
                  size="small"
                  label="Digital Release Day (UTC)"
                  type="date"
                  value={digitalScheduledDate}
                  onChange={(e) => setDigitalScheduledDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={!includeDigital || isSubmitting}
                  sx={{ minWidth: 220 }}
                />

                <TextField
                  size="small"
                  label="Digital Release Time (UTC)"
                  type="time"
                  value={digitalScheduledTimeUtc}
                  onChange={(e) => setDigitalTimeUtc(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={!includeDigital || isSubmitting}
                  inputProps={{ step: 300 }}
                  sx={{ minWidth: 180 }}
                />

                <Chip
                  size="small"
                  label={`Digital: ${counts.Digital.selected}/${counts.Digital.total}`}
                />
              </Box>

              {includeDigital && digitalPreview && (
                <Box sx={{ pl: 0.5 }}>
                  <Typography variant="caption" sx={{ display: 'block', color: colors.grey[200] }}>
                    UTC: {digitalPreview.utcIso}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: colors.grey[300] }}>
                    Your time ({digitalPreview.localTimeZone}): {digitalPreview.localLabel}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>

          <Divider />

          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            sx={{
              '& .MuiTab-root': {
                color: colors.grey[100],
                fontWeight: 700,
              },
              '& .Mui-selected': {
                color: colors.greenAccent[300],
              },
              '& .MuiTabs-indicator': {
                backgroundColor: colors.greenAccent[400],
                height: 3,
              },
            }}
          >
            <Tab label="Contact Products" disabled={isSubmitting} />
            <Tab label="Digital Products" disabled={isSubmitting} />
          </Tabs>

          <TabPanel value={tab} index={0}>
            <Typography variant="caption" sx={{ color: colors.grey[300] }}>
              Default: all products included. Remove/re-add with the button — versions are selected later.
            </Typography>
            <Box mt={1}>{renderProductList('Contact')}</Box>
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <Typography variant="caption" sx={{ color: colors.grey[300] }}>
              Default: all products included. Remove/re-add with the button — versions are selected later.
            </Typography>
            <Box mt={1}>{renderProductList('Digital')}</Box>
          </TabPanel>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{
            fontWeight: 700,
            backgroundColor: '#ffffff',
            color: colors.redAccent[500],
            border: `2px solid ${colors.redAccent[500]}`,
            '&:hover': {
              backgroundColor: colors.redAccent[900],
              color: '#ffffff',
              borderColor: colors.redAccent[400],
            },
          }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          disabled={!canCreate}
          onClick={handleCreate}
          startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {isSubmitting ? 'Creating Release...' : 'Create Release'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateReleaseDialog;